/**
 * 게시물 지표 스냅샷 한 건을 고르는 규칙.
 *
 * 「정렬 스냅샷 기준」(`getMetricsByScoreTime`)과 「가장 최근 수집일 기준」
 * (`getMetricsByLatestDate`) 둘이 있고, 화면마다 어느 쪽을 쓰는지가 다르다.
 *
 * ⚠️ `getMetricsByScoreTime` 의 시그니처와 옵션 키(`useMetricDetailSort`)를 바꾸면
 * 카드 수치와 정렬 순서가 어긋난다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';
import type { MomentMetricEntry, MetricsSortSnapshot } from 'src/types/moments/moment-domain';

/**
 * 지표 스냅샷 한 건 — `getMetricsByLatestDate`의 반환.
 *
 * `requestDateTime`은 **문자열이다.** 구현이 `latest.requestDateTime`(`MomentMetricEntry`,
 * string) → `post.metricScoreTime`(string) → `''` 로만 폴백한다.
 */
export type PostMetricSnapshot = {
  likeCount: number;
  commentCount: number;
  viewCount: number;
  savedCount: number;
  shareCount: number;
  downloadCount: number;
  requestDateTime: string;
};

/**
 * `getMetricsByScoreTime`의 반환 — 위와 같되 **`requestDateTime`이 숫자일 수 있다.**
 *
 * 이 함수만 `metricScoreSort.metricDate`(`string | number`)로 폴백하기 때문이다(아래 구현 참조).
 * ⚠️ 두 함수가 타입 하나를 공유하게 만들지 마라 — `metricsSort`(ES 필드,
 * `requestDateTime?: string`)에 `getMetricsByLatestDate` 결과를 써 넣는 자리가 막힌다.
 */
export type PostMetricSnapshotByScoreTime = Omit<PostMetricSnapshot, 'requestDateTime'> & {
  requestDateTime: string | number;
};

/** ES 지표 맵/배열을 순회 가능한 엔트리 배열로 (둘 다 실제로 온다) */
const toMetricEntries = (
  metrics: SocialPostInput['metrics'],
): (MomentMetricEntry | null)[] =>
  (Array.isArray(metrics) ? metrics : Object.values(metrics ?? {}));

/**
 * requestDateTime 값을 숫자 타임스탬프(ms)로 변환 (최신/가장 빠른 날짜 기준 함수용)
 * @param {string|number} value - ISO 문자열, Unix 초, "YYYY-MM-DD HH:mm:ss" 등
 * @returns {number} ms 단위 타임스탬프 (유효하지 않으면 0)
 */
function parseRequestDateTimeToMs(value: unknown): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') {
    return value < 1e12 ? value * 1000 : value;
  }
  const str = String(value).trim();
  if (!str) return 0;
  if (/^\d{8}$/.test(str)) {
    const y = Number(str.slice(0, 4));
    const m = Number(str.slice(4, 6));
    const d = Number(str.slice(6, 8));
    return new Date(y, m - 1, d).getTime();
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * 정렬(metricScoreSort) 기준과 같은 시점의 세부 수치 스냅샷 = `metricsSort`.
 *
 * **이 필드만 쓴다 — 폴백 없음. 없으면 0이다.**
 * 구 이름 `metricDetailSort`는 읽지 않는다: ES에서 미색인이라 집계·정렬에 쓸 수 없고
 * (`exists` 0건 / `sum` 0), 값도 `metricsSort`와 동일해 이중 관리만 된다.
 *
 * 기준은 ES 집계다 — `sum: { field: 'metricsSort.viewCount' }`가 필드 없는 문서를 0으로
 * 더하므로, 표시·Excel도 같은 규칙이어야 화면 합계와 ES 집계가 맞는다.
 * `metrics`(일자별)·루트 카운트로 내려가는 경로는 `useMetricDetailSort: false`를 넘기는
 * 호출부(캠페인 집계 등)에만 남아 있다.
 *
 * 루트 `viewCount`·`likeCount`는 "최신 수집값"이라 `metricScoreSort`(D+N 컷오프 시점)와
 * 기준이 다르다. 둘을 섞으면 "총 조회수 > 총 도달" 같은 역전이 생긴다.
 *
 * ES 집계(sum)·정렬도 같은 기준으로 맞춰 뒀다 —
 * `constants/metrics-sort-fields.ts`의 스위치 참조.
 */
export function getSortAlignedMetricSnapshot(
  post?: SocialPostInput | null,
): MetricsSortSnapshot | null {
  const value = post?.metricsSort;
  if (!value || typeof value !== 'object') return null;
  if (!Array.isArray(value)) return value;
  // ES _source는 같은 경로에 값이 여러 개면 배열로 내려준다 (실측상 단일 객체지만 방어).
  // 그 경우 requestDateTime이 가장 늦은(= 컷오프에 가장 가까운) 항목을 고른다.
  const entries = value.filter((v): v is MetricsSortSnapshot => Boolean(v) && typeof v === 'object');
  if (entries.length === 0) return null;
  return entries.reduce((acc, cur) => (
    parseRequestDateTimeToMs(cur?.requestDateTime ?? cur?.metricDate)
      > parseRequestDateTimeToMs(acc?.requestDateTime ?? acc?.metricDate)
      ? cur
      : acc
  ));
}

/**
 * 게시물의 메트릭 반응 값 추출
 * metrics 객체에서 requestDateTime이 가장 빠른(가장 이른) 항목의 값을 사용
 * @param {Object} post - 게시물 객체 (metrics, likeCount, commentCount, viewCount 포함)
 * @param {Object} post.metrics - 메트릭 정보 객체
 * @param {{ useMetricDetailSort?: boolean }} [options] - useMetricDetailSort가 false면 정렬 스냅샷(metricsSort) 분기 생략(PostDetail 등 기존 표시 유지)
 * @returns {Object} { likeCount, commentCount, viewCount, savedCount, shareCount, downloadCount, requestDateTime }
 */
export const getMetricsByScoreTime = (
  post: SocialPostInput | null | undefined,
  options: { useMetricDetailSort?: boolean } = {},
): PostMetricSnapshotByScoreTime => {
  const { useMetricDetailSort = true } = options;
  if (post?.isRepost === true) {
    return {
      likeCount: 0,
      commentCount: 0,
      viewCount: 0,
      savedCount: 0,
      shareCount: 0,
      downloadCount: 0,
      requestDateTime: post?.metricScoreTime ?? '',
    };
  }
  if (useMetricDetailSort) {
    // ES 집계와 동일 규칙: metricsSort가 있으면 그 안의 값만, 없으면 0.
    // sum: { field: 'metricsSort.viewCount' }가 필드 없는 문서를 0으로 더하는 것과 맞춘다 —
    // 여기서만 metrics·루트로 폴백하면 화면 합계와 ES 집계가 어긋난다.
    const sortAligned = getSortAlignedMetricSnapshot(post);
    return {
      likeCount: Number(sortAligned?.likeCount ?? 0) || 0,
      commentCount: Number(sortAligned?.commentCount ?? 0) || 0,
      viewCount: Number(sortAligned?.viewCount ?? 0) || 0,
      savedCount: Number(sortAligned?.savedCount ?? 0) || 0,
      shareCount: Number(sortAligned?.shareCount ?? 0) || 0,
      downloadCount: Number(sortAligned?.downloadCount ?? 0) || 0,
      requestDateTime:
        sortAligned?.requestDateTime ??
        sortAligned?.metricDate ??
        post?.metricScoreTime ??
        '',
    };
  }

  if (post && post.metrics && typeof post.metrics === 'object') {
    const validEntries = toMetricEntries(post.metrics).filter((entry) => entry != null);

    if (validEntries.length > 0) {
      const datedEntries = validEntries.filter(
        (m) => m?.requestDateTime != null || m?.metricScoreTime != null
      );

      const latest = datedEntries.length > 0
        ? datedEntries.reduce((acc, m) => {
          const accMs = parseRequestDateTimeToMs(acc?.requestDateTime ?? acc?.metricScoreTime);
          const mMs = parseRequestDateTimeToMs(m?.requestDateTime ?? m?.metricScoreTime);
          return mMs > accMs ? m : acc;
        })
        : validEntries[validEntries.length - 1];

      return {
        likeCount: latest.likeCount ?? post.likeCount ?? 0,
        commentCount: latest.commentCount ?? post.commentCount ?? 0,
        viewCount: latest.viewCount ?? post.viewCount ?? 0,
        savedCount: latest.savedCount ?? post.savedCount ?? 0,
        shareCount: latest.shareCount ?? post.shareCount ?? 0,
        downloadCount: latest.downloadCount ?? post.downloadCount ?? 0,
        requestDateTime: latest.requestDateTime ?? latest.metricScoreTime ?? post?.metricScoreTime ?? '',
      };
    }
  }
  return {
    likeCount: post?.likeCount ?? 0,
    commentCount: post?.commentCount ?? 0,
    viewCount: post?.viewCount ?? 0,
    savedCount: post?.savedCount ?? 0,
    shareCount: post?.shareCount ?? 0,
    downloadCount: post?.downloadCount ?? 0,
    requestDateTime: post?.metricScoreTime || '',
  };
};

/**
 * 게시물의 메트릭 반응 값 추출 (최신 날짜 기준)
 * metrics 내 requestDateTime이 가장 최신인 항목의 값을 반환
 * @param {Object} post - 게시물 객체 (metrics, likeCount, commentCount, viewCount 등 포함)
 * @param {Object} post.metrics - 메트릭 정보 객체 (키별로 requestDateTime 포함)
 * @returns {Object} { likeCount, commentCount, viewCount, savedCount, shareCount, downloadCount, requestDateTime }
 */
export function getMetricsByLatestDate(
  post: SocialPostInput | null | undefined,
): PostMetricSnapshot {
  if (post && post.metrics && typeof post.metrics === 'object') {
    const entries = toMetricEntries(post.metrics)
      .filter((entry) => entry != null)
      .filter((entry) => entry.requestDateTime != null);
    if (entries.length > 0) {
      const latest = entries.reduce((acc, m) => {
        const accMs = parseRequestDateTimeToMs(acc?.requestDateTime);
        const mMs = parseRequestDateTimeToMs(m?.requestDateTime);
        return mMs > accMs ? m : acc;
      });
      return {
        likeCount: latest.likeCount ?? post.likeCount ?? 0,
        commentCount: latest.commentCount ?? post.commentCount ?? 0,
        viewCount: latest.viewCount ?? post.viewCount ?? 0,
        savedCount: latest.savedCount ?? post.savedCount ?? 0,
        shareCount: latest.shareCount ?? post.shareCount ?? 0,
        downloadCount: latest.downloadCount ?? post.downloadCount ?? 0,
        requestDateTime: latest.requestDateTime ?? post.metricScoreTime ?? '',
      };
    }
  }
  return {
    likeCount: post?.likeCount ?? 0,
    commentCount: post?.commentCount ?? 0,
    viewCount: post?.viewCount ?? 0,
    savedCount: post?.savedCount ?? 0,
    shareCount: post?.shareCount ?? 0,
    downloadCount: post?.downloadCount ?? 0,
    requestDateTime: post?.metricScoreTime || '',
  };
}
